import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import AddictionService from "./AddictionService";
import TrendModal from "./TrendModal";
import { addictionBand, formatDuration, isZeroDuration } from "./data";
import { ADD_V2_EDIT, ADD_V2_REFRESH } from "./events";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * Addictions — content column, backed by api-addiction-v2-service.
 *
 *   • Fixed 4-entry list (Smoking / Alcohol / Drugs / Tobacco) — no
 *     add-new-row or delete flow.
 *   • ONE update form (AddictionForm), opened by clicking either the
 *     addiction name OR the status chip. It lives in the EDITOR RAIL — the
 *     third column on the right — not in a centred modal, per board
 *     3071:2446, so the table stays readable while it is open and several
 *     addictions can be queued. Handles status + status update date +
 *     started when + total interval + frequency + description.
 *   • Per-row "View trend" link (Analysis column) opens the audit trail
 *     with a mark-as-incorrect review flow (mirrors the Allergies section).
 *
 * Columns:
 *   Addiction · Started when · Frequency · Total interval · Description ·
 *   Status · Analysis
 */

// Detail cells show em-dash for Never so the row visually collapses.
const detailText = (a, field, formatter) => {
  if (a.status === "never") return "—";
  const v = a[field];
  return formatter ? formatter(v) : v || "—";
};

// Total interval cell. When the field is left blank (only allowed for
// non-quit statuses — Quit requires it explicitly) the table shows the
// live "till today" value derived from Started when. Started when is
// already stored as "how long ago", so mirroring it gives the correct
// on-going interval without any date arithmetic. Falls back to em-dash
// when Started when is also blank.
/* The board paints EVERY empty cell with the same #c7cad0 dash rather than
   letting it inherit the column's own ink. */
const Dash = ({ children }) =>
  children === "—" ? <span className="ad3-dash">&mdash;</span> : children;
Dash.propTypes = { children: PropTypes.node };

const totalIntervalText = (a) => {
  if (a.status === "never") return "—";
  if (!isZeroDuration(a.totalInterval)) {
    return formatDuration(a.totalInterval, false);
  }
  if (!isZeroDuration(a.startedWhen)) {
    return formatDuration(a.startedWhen, false);
  }
  return "—";
};

/* Board: a 16x16 stroked chevron whose stroke is the band's own label colour,
   not the 8px U+25BE glyph. */
const ChipCaret = () => (
  <svg viewBox="0 0 11.33 6.67" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M10.3333 1L5.66667 5.66667L1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Scoped to ad3-*: .tx-chip is shared with Medicines and Physical activity and
   must keep its hug-content box. */
const StatusChip = ({ status, onClick, disabled }) => {
  const band = addictionBand(status || "never");
  return (
    <button
      type="button"
      className="ad3-chip"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      disabled={disabled}
      title="Update"
    >
      <span className="ad3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="ad3-chip-label">{band.label}</span>
      <ChipCaret />
    </button>
  );
};
StatusChip.propTypes = {
  status: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const Addictions = ({ patient, capturedItems }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendTarget, setTrendTarget] = useState(null);

  // The row-name click and the status-chip click both funnel through here.
  // The rail owns the sheet (and its de-duplication), so this only announces
  // the intent — it deliberately keeps no target of its own.
  const openEditor = useCallback((addiction) => {
    EventService.emit(ADD_V2_EDIT, { addiction });
  }, []);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new AddictionService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { addictions: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load addictions");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(ADD_V2_REFRESH, load);
    return () => EventService.removeListener(ADD_V2_REFRESH, load);
  }, [load]);

  const addictionsRaw = (doc && doc.addictions) || [];

  // Transcript-mode overlay. Addictions is a fixed 4-slot list — the
  // rows themselves never grow, so we don't merge staged rows the way
  // the other sections do. Instead we tag any persisted row that has a
  // captured update pending, so the reviewer can spot which of the four
  // has a proposed change to review on the right column.
  const capturedByKey = useMemo(() => {
    const map = {};
    if (!capturedItems) return map;
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      const key = it.payload && (it.payload.key || it.payload.value);
      if (typeof key === "string" && key) map[key.toLowerCase()] = it;
    }
    return map;
  }, [capturedItems]);
  const addictions = useMemo(
    () =>
      addictionsRaw.map((a) => {
        const it = capturedByKey[String(a.key || "").toLowerCase()];
        if (!it) return a;
        return { ...a, _captured: it, _capturedIsAi: (it.confidence || 0) > 0 };
      }),
    [addictionsRaw, capturedByKey]
  );

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading addictions…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="ad3-card">
      <header className="ad3-toolbar">
        <h2 className="ad3-title">Addictions</h2>
        {/* Board 3071:1858 — the middot and its trailing space are part of the
            string; the toolbar's 9px gap supplies the space before it. */}
        <span className="ad3-count">&middot; {addictions.length} entries</span>
      </header>

      <div className="ad3-table" role="table">
        <div className="ad3-thead" role="row">
            <span role="columnheader">Addiction</span>
            <span role="columnheader">Started when</span>
            <span role="columnheader">Frequency</span>
            <span role="columnheader">Total interval</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Analysis</span>
          </div>

        {addictions.map((a) => (
          <div
            className={`ad3-row${a._captured ? " is-captured" : ""}`}
            role="row"
            key={a.key}
          >
            <span className="ad3-cell ad3-c-name" role="cell">
              <button
                type="button"
                className="ad3-name-btn"
                  onClick={() => openEditor(a)}
                  title={`Update ${a.label}`}
                >
                  {a._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                  {a.label}
                </button>
              </span>
            <span className="ad3-cell ad3-c-started" role="cell">
              <Dash>{detailText(a, "startedWhen", formatDuration)}</Dash>
            </span>
            <span className="ad3-cell ad3-c-freq" role="cell">
              {/* Same clamp + hover-when-truncated tooltip Description
                  uses. `never` status still renders the ad3-dash via
                  detailText → Dash. */}
              {(() => {
                const text = detailText(a, "frequency");
                return text === "—" ? (
                  <Dash>{text}</Dash>
                ) : (
                  <MetaTooltip text={text} lines={2} />
                );
              })()}
            </span>
            <span className="ad3-cell ad3-c-total" role="cell">
              <Dash>{totalIntervalText(a)}</Dash>
            </span>
            <span className="ad3-cell ad3-c-desc" role="cell">
              {/* 3-line clamp + white hover tooltip; only reveals when
                  the text is actually being truncated. Same pattern
                  Family history uses. */}
              <MetaTooltip text={a.description || ""} lines={3} />
            </span>
            <span className="ad3-cell ad3-c-status" role="cell">
              <StatusChip status={a.status} onClick={() => openEditor(a)} />
            </span>
            <span className="ad3-cell ad3-c-analysis" role="cell">
              <button
                type="button"
                className="ad3-trend-link"
                onClick={() => setTrendTarget(a)}
                title="View audit history"
              >
                View trend
              </button>
            </span>
          </div>
        ))}
      </div>

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          addictionKey={trendTarget.key}
          label={trendTarget.label}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

Addictions.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
};

export default Addictions;
